import './style/Offers.css';
import React, { useEffect, useMemo, useCallback } from 'react';
import Icon from './Icon';
import network from '../../media/network@3x.png';
import { useSelector, useDispatch } from 'react-redux';
import { openTrayTypeWithData } from '../../redux/keystoneSlice';
import Placeholder from './Placeholder';
import Button from './Button';
import { useEvent as useEffectEvent } from '@reactuses/core';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchOffers, normalizeTabName } from '../../redux/keystoneInterfaceSlice';
import { Text } from '@harperai/materialize-design-system';
import { colorFromNumber, expiresInTextFromDate, formatToCurrency } from '../../util/Calculate';
import OfferActive from '../../media/offer_active.png';
import OfferAccepted from '../../media/offer_accepted.png';
import OfferRejected from '../../media/offer_rejected.png';
import OfferCompleted from '../../media/offer_completed.png';

const Offers = () => { 
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const keystoneInterfaceFetchingCompany = useSelector((state) => state?.keystoneInterface?.fetchingCompany);
    const keystoneInterfaceOffersAccepted = useSelector((state) => state?.keystoneInterface?.offersAccepted);
    const keystoneInterfaceOffersTabs = useSelector((state) => state?.keystoneInterface?.offersTabs);
    const keystoneInterfaceAccessToken = useSelector((state) => state?.keystoneInterface?.accessToken);
    const keystoneInterfaceFetchedOffers = useSelector((state) => state?.keystoneInterface?.fetchedOffers);

    const { page, tabName } = useParams();

    const keystoneInterfaceOffersTab = keystoneInterfaceOffersTabs.map(normalizeTabName).indexOf(normalizeTabName(tabName));
    const keystoneInventoryOffers = useSelector((state) => state?.keystoneInventory?.offers);

    const { search } = useLocation();
    const selectedObjectId = new URLSearchParams(search).get('id') || null;
    const selectedObject = useSelector((state) => state?.keystoneInventory?.offers?.find(offer => offer.id === selectedObjectId));

    useEffect(() => {
        if (keystoneInterfaceOffersTab < 0 && !selectedObjectId) {
            navigate(`/${page}/${normalizeTabName(keystoneInterfaceOffersTabs?.[0])}`);
        }
    }, [keystoneInterfaceOffersTab, keystoneInterfaceOffersTabs, page, navigate, selectedObjectId]);

    useEffect(() => {
        fetchOfferData();
    }, []);

    useEffect(() => {
        const matchingOffer = keystoneInventoryOffers?.find(offer => offer.id === selectedObjectId);
        if (!matchingOffer && selectedObjectId && keystoneInterfaceFetchedOffers) {
            navigate(`/${page}/${tabName}`);
        } else if (matchingOffer?.status === "AWAITING_RESPONSE" && tabName !== "active") {
            navigate(`/${page}/active?id=${matchingOffer.id}`);
        } else if (matchingOffer?.status === "ACCEPTED" && matchingOffer?.payout_status !== "SENT" && tabName !== "accepted") {
            navigate(`/${page}/accepted?id=${matchingOffer.id}`);
        } else if (matchingOffer?.payout_status === "SENT" && tabName !== "completed") {
            navigate(`/${page}/completed?id=${matchingOffer.id}`);
        } else if ((matchingOffer?.status === "REJECTED" || matchingOffer?.status === "EXPIRED") && tabName !== "rejected") {
            navigate(`/${page}/rejected?id=${matchingOffer.id}`);
        }
    }, [keystoneInterfaceFetchedOffers, keystoneInventoryOffers, navigate, page, selectedObjectId, tabName]);

    const fetchOfferData = useEffectEvent(() => {
        dispatch(fetchOffers({ accessToken: keystoneInterfaceAccessToken }));
    });

    useEffect(() => {
        if (selectedObject) {
            dispatch(openTrayTypeWithData({
                trayOpen: true,
                trayData: selectedObject,
                trayType: "offer"
            }));
        }
    }, [dispatch, selectedObject]);

    const handleOnClick = useCallback((offer) => {
        navigate(`/${page}/${tabName}?id=${offer.id}`);
    }, [navigate, page, tabName]);

    const OfferIcon = ({ offer }) => {
        const { hue, sat, light } = colorFromNumber(offer?.offer_number);
        const tint = `hsl(${hue} ${sat}% ${light}%)`;
        const src =
            tabName === 'active' ? OfferActive : tabName === "accepted" ? OfferAccepted : tabName === "rejected" ? OfferRejected : OfferCompleted;

        return (
            <div style={{position:'relative', width:64}}>
                <img
                    src={src}
                    style={{width:'100%', display:'block'}}
                    alt=""
                />

                <div
                    style={{
                        position:'absolute',
                        inset:0,
                        backgroundColor:tint,
                        mixBlendMode:'color',
                        borderRadius:6,
                        pointerEvents:'none',
                        opacity: 0.8
                    }}
                />
            </div>
        );
    };

    const offers = useMemo(() => {
        return keystoneInventoryOffers?.filter(offer =>
                            (keystoneInterfaceOffersTab === 0 && offer.status === "AWAITING_RESPONSE") ||
                            (keystoneInterfaceOffersTab === 1 && (offer.status === "ACCEPTED" && offer.payout_status !== "SENT")) ||
                            (keystoneInterfaceOffersTab === 2 && offer.payout_status === "SENT") ||
                            (keystoneInterfaceOffersTab === 3 && (offer.status === "REJECTED" || offer.status === "EXPIRED"))).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    }, [keystoneInterfaceOffersTab, keystoneInventoryOffers]);

    return (
        <div className="keystone-offers product-horizontal-container space-between h-gap-l width-100 overflow-auto">
            <div className="product-vertical-container flex-1 v-gap-l">
                <div className="pad-l no-pad-bot">
                    <Text header={2} size="h2" bold={true}>{tabName === "active" ? "Active Offers" : tabName === "accepted" ? "Accepted" : tabName === "completed" ? "Completed" : "Rejected"}</Text>
                </div>
                {!keystoneInterfaceFetchingCompany && !keystoneInterfaceOffersAccepted && (
                    <div className={`offers-promo product-vertical-container v-gap-l ${page === "offers" && 'margin-32'}`}>
                        <div className="product-horizontal-container h-gap-xs relative space-between">
                            <div className="product-vertical-container v-gap-xs flex-1">
                                <Text header={3} size="paragraph" bold={true}>Inventory Access</Text>
                                <Text>Your inventory is hidden by default from prospective buyers.</Text>
                            </div>
                            <Icon icon={network} size={28} stylisticFill={true} />
                        </div>
                        <div className="product-horizontal-container justify-end h-gap-l">
                            <Button label="Go to Setting" destinationUrl={"/manage/organization"} onBrand={true} />
                        </div>
                    </div>
                )}
                {keystoneInterfaceOffersAccepted && (
                    <div className="product-vertical-container list-container pad-m no-pad-top v-gap-s overflow-auto">
                        {offers?.map((offer, index) => (
                            <div className={`flex horizontal keystone-card keystone-row ${selectedObjectId === offer.id ? "keystone-row-active" : ""} space-between pad-m clickable`} key={index} onClick={(e) => handleOnClick(offer)}>
                                <div className="flex horizontal gap-l">
                                    <OfferIcon offer={offer} />
                                    <div className="flex vertical">
                                        <div className="flex horizontal gap-s">
                                            <Text  size="paragraph" mono={true} bold={true}>{offer.offer_number.toString().padStart(12,'0').replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3')}</Text>
                                        </div>
                                        <Text size="caption" variant="secondary" bold={true}>Items: {offer.offer_items?.reduce((acc, item) => acc + item.quantity, 0)} • Locations: {offer.unique_locations?.length}</Text>
                                    </div>
                                    {offer.expiration_date && (offer.status === "AWAITING_RESPONSE" || offer.status === "EXPIRED") && (
                                        <div className="status-chip flex horizontal grey-bg round-s gap-m">
                                            <div class={`small-circle ${expiresInTextFromDate(offer.expiration_date).includes("Expired") ? "circle-negative" : "circle-warning"}`}/>
                                            <Text size="micro" bold={true} mono={true}>{expiresInTextFromDate(offer.expiration_date)}</Text>
                                        </div>
                                    )}
                                    {offer.offer_type === "BUY_IT_NOW" && (
                                        <div className="status-chip flex horizontal grey-bg round-s gap-m">
                                            <Text size="micro" bold={true} mono={true}>Auto-Accepted</Text>
                                        </div>
                                    )}
                                </div>
                                <div className="flex vertical align-end">
                                    <Text size="paragraph" mono={true} bold={true}>{formatToCurrency(offer.total_price)}</Text>
                                    <Text size="caption" variant="secondary" bold={true}>Total Return</Text>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {keystoneInterfaceOffersAccepted &&
                keystoneInterfaceOffersTab === 0 &&
                !keystoneInventoryOffers?.some(offer => (
                    offer.status === "AWAITING_RESPONSE")
                ) && (
                    <Placeholder label="You're all caught up!" />
                )}
                {keystoneInterfaceOffersAccepted &&
                keystoneInterfaceOffersTab === 1 &&
                !keystoneInventoryOffers?.some(offer => (
                    offer.status === "ACCEPTED")
                ) && (
                    <Placeholder label="You're all caught up!" />
                )}
                {keystoneInterfaceOffersAccepted &&
                keystoneInterfaceOffersTab === 2 &&
                !keystoneInventoryOffers?.some(offer => (
                    offer.payout_status === "SENT"
                )) && (
                    <Placeholder label="You're all caught up!" />
                )}
                {keystoneInterfaceOffersAccepted &&
                keystoneInterfaceOffersTab === 3 &&
                !keystoneInventoryOffers?.some(offer => (
                    offer.status === "REJECTED"
                )) && (
                    <Placeholder label="You're all caught up!" />
                )}
            </div>
        </div>
    );
};

export default React.memo(Offers);